// Surge aura — P1 spec (2026-11-02) layering, owner fire pass (2026-11-02: "a fire flame around the character, like a
// flame animation, with those colours"): a 12-sided tapered shell + one cylindrical-billboard flame card + an angled
// second card, all drawing anime cel FIRE from a procedural flipbook atlas (public/textures/aura_fire_atlas.png,
// scripts/gen-aura-textures.py: 64 frames, seamless loop, tongues that drift, pulse, stretch and peel off as embers).
// The shader cross-fades frames, warps them with two scrolling noises and posterises the heat channel into hard cel
// bands (white-hot core -> level colour -> dark rim). Plus a faint inner core glow, instanced rising streaks and
// pulsing ground rings. Alpha is the silhouette mask and emission sits on top, so it reads with bloom off. Render-only,
// GPU-driven from `time`: no per-frame CPU work or allocation. Units: the table is in metres; M converts to rig units.
import * as THREE from 'three/webgpu';
import {
  abs, attribute, cameraProjectionMatrix, cameraWorldMatrix, clamp, cos, cross, dot, float, floor, fract, max, min,
  mix, mod, modelViewMatrix, modelWorldMatrixInverse, normalize, oneMinus, positionLocal, sin, smoothstep, step,
  texture, time, uv, varying, vec2, vec3, vec4,
} from 'three/tsl';
import { softParticles } from 'three/addons/tsl/utils/SoftParticles.js';

/** uniforms are owned by SurgeVisual; typed loosely so the TSL generics stay out of the public API */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type U = any;
export interface AuraUniforms {
  /** colour ramp: core / mid / outer */
  core: U; mid: U; outer: U;
  /** stage alpha (already enveloped by fade-in/out + flicker), emission gain, core strength */
  alpha: U; emission: U; coreK: U;
  /** flipbook rate (0.24 = 12 fps), noise tilings (kept for the API), warp amplitudes */
  speed: U; coarse: U; fine: U; warpX: U; warpY: U;
  /** shell: base radius + height (m); cards: width + height (m) */
  shellR: U; shellH: U; cardW: U; cardH: U;
  /** inner core glow alpha, streak pool fraction alive, ground rings per second, ring strength */
  glow: U; streaks: U; ringRate: U; rings: U;
}

/** soles in hero-rig units (rig root = hip; hero ~2.57 units = ~1.8 m tall) */
export const AURA_SOLE = -1.12;
/** rig units per metre */
export const M = 1.43;
const TAU = Math.PI * 2;
const STREAK_POOL = 8, RING_POOL = 3, RING_LIFE = 0.33;
/** fire flipbook atlas: frames, grid, base frame rate */
const FRAMES = 64, GRID = 8, FPS = 12;

const mat = () => new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
/** vec3/vec4 constructors without the over-strict TSL argument typing (mixed float/vec2 args) */
const V3: U = vec3, V4: U = vec4;
const hash = (x: U) => fract(sin(x.mul(12.9898)).mul(43758.5453));
const vary = (x: U, name: string): U => varying(x, name);
const FEET = vec3(0, AURA_SOLE, 0);

// ---------------------------------------------------------------- textures (shared by every SurgeVisual)
export interface AuraTextures { fire: THREE.Texture; noise: THREE.Texture; streak: THREE.Texture; ring: THREE.Texture }
let _tex: AuraTextures | null = null;
export function auraTextures(): AuraTextures {
  if (_tex) return _tex;
  const loader = new THREE.TextureLoader();
  const load = (name: string, wrap: THREE.Wrapping) => {
    const t = loader.load(`${import.meta.env.BASE_URL}textures/${name}`);
    t.wrapS = t.wrapT = wrap;
    t.generateMipmaps = false; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; // mips put a seam on the strip
    t.colorSpace = THREE.NoColorSpace; // linear data
    return t;
  };
  _tex = { fire: load('aura_fire_atlas.png', THREE.ClampToEdgeWrapping), noise: load('aura_noise_rg.webp', THREE.RepeatWrapping),
    streak: load('aura_streak_mask.png', THREE.ClampToEdgeWrapping), ring: load('aura_ring.png', THREE.ClampToEdgeWrapping) };
  return _tex;
}

/** QA knob: lean of the flame cards from vertical toward the camera's screen-up (spec default 0 = cylindrical billboard) */
// default 0.45 (lead, 2026-11-02): strictly vertical cards foreshorten into a faint smudge at the 55° gameplay camera;
// a partial lean toward screen-up keeps the flame shape readable there. ?auralean=0 restores P1's strictly vertical cards.
const LEAN_Q = new URLSearchParams(location.search).get('auralean');
const LEAN = Math.min(1, Math.max(0, LEAN_Q === null ? 0.45 : Number(LEAN_Q) || 0));
const camUpLocal = (): U => normalize((modelWorldMatrixInverse as U).mul((cameraWorldMatrix as U).mul(vec4(0, 1, 0, 0))).xyz);

/** camera right / back in model space, horizontal (cylindrical billboard: the layer rotates about world Y only) */
function cylFrame(): { right: U; back: U } {
  const inv: U = modelWorldMatrixInverse, cam: U = cameraWorldMatrix;
  const camBack = inv.mul(cam.mul(vec4(0, 0, 1, 0))).xyz;
  const back: U = normalize(V3(camBack.x, 0, camBack.z));
  const right: U = normalize(cross(vec3(0, 1, 0), back));
  return { right, back };
}

/** Flipbook fire in fire space f = (x periodic, y 0 at the base .. 1 at the top): cross-faded frames, warped by two
 *  scrolling noises. Returns heat (0..1), the hard silhouette alpha and the ember mask. */
function fire(u: AuraUniforms, tex: AuraTextures, f: U, phase: number, sideways = 1): { heat: U; alpha: U; ember: U } {
  const n1: U = texture(tex.noise, f.mul(vec2(3, 2)).add(vec2(0.03, time.mul(-0.3)))).r.mul(2).sub(1);
  const n2: U = texture(tex.noise, f.mul(vec2(4, 3)).add(vec2(time.mul(0.07), time.mul(-0.5)))).g.mul(2).sub(1);
  const x = fract(f.x.add(n1.mul(u.warpX).mul(sideways))), y = clamp(f.y.add(n2.mul(u.warpY)), 0.004, 0.996);
  // frame cursor: 12 fps at speed 0.24, faster for the higher levels
  const fr = time.mul(u.speed.div(0.24)).mul(FPS).add(phase);
  const ff = fract(fr.div(FRAMES)).mul(FRAMES);
  const f0 = floor(ff), f1 = mod(f0.add(1), FRAMES), blend = fract(ff);
  const cell = (fi: U): U => { const col = mod(fi, GRID), row = floor(fi.div(GRID)); return vec2(col.add(x).div(GRID), float(GRID - 1).sub(row).add(y).div(GRID)); };
  const s0: U = texture(tex.fire, cell(f0)), s1: U = texture(tex.fire, cell(f1));
  return { heat: mix(s0.r, s1.r, blend), alpha: mix(s0.a, s1.a, blend), ember: mix(s0.g, s1.g, blend) };
}

/** anime cel fire: hard bands, white-hot core -> level colour -> dark rim; embers are white-hot */
function cel(u: AuraUniforms, heat: U, ember: U, alpha: U): U {
  const core = smoothstep(0.78, 0.83, heat), mid = smoothstep(0.46, 0.51, heat); // white-hot only at the base, as tongue-shaped intrusions
  const rim = oneMinus(smoothstep(0.55, 0.72, alpha)); // thin dark outline just inside the silhouette edge
  let col: U = mix(u.outer, u.mid, mid);
  col = mix(col, u.core, max(core.mul(u.coreK), ember));
  col = mix(col, u.outer.mul(0.7), rim.mul(0.6));
  return col.mul(u.emission);
}
/** hard-edged silhouette from the cross-faded alpha */
const celAlpha = (alpha: U): U => smoothstep(0.3, 0.55, alpha);

const soft = (alpha: U): U => softParticles({ opacity: alpha, distance: 0.25, contrast: 1.8 });

// ---------------------------------------------------------------- 1. tapered 12-sided shell: a ring of fire tongues
export function buildAuraShell(u: AuraUniforms, tex: AuraTextures): THREE.Mesh {
  const m = mat();
  const geo = new THREE.CylinderGeometry(0.38 / 0.55, 1, 1, 12, 1, true); geo.translate(0, 0.5, 0); // base radius 1, height 1, y 0..1
  const p: U = positionLocal;
  const P = FEET.add(V3(p.x.mul(u.shellR).mul(M), p.y.mul(u.shellH).mul(M), p.z.mul(u.shellR).mul(M)));
  const mv: U = modelViewMatrix;
  const posV = mv.mul(V4(P, 1)).xyz, nV = mv.mul(V4(normalize(V3(p.x, 0, p.z)), 0)).xyz;
  m.vertexNode = cameraProjectionMatrix.mul(V4(posV, 1));
  // the wall between the camera and Aren is faded so his outline stays readable
  const facing = vary(dot(normalize(nV), normalize(posV.negate())), 'vShellFace');
  // two repeats of the (periodic) fire around the hero; the frames' solid base is a short skirt, the rest is tongues
  const { heat, alpha, ember } = fire(u, tex, vec2(uv().x.mul(2), uv().y), 7);
  m.colorNode = cel(u, heat, ember, alpha);
  // the far wall is a fire backdrop behind Aren; the near wall is faint and the grazing silhouette walls fade so they
  // never draw as bright vertical lines
  const near = oneMinus(smoothstep(0.0, 0.6, facing).mul(0.6));
  const edgeFade = smoothstep(0.1, 0.5, abs(facing));
  const vf = smoothstep(0, 0.04, uv().y);
  m.opacityNode = soft(celAlpha(alpha).mul(u.alpha).mul(0.55).mul(near).mul(edgeFade).mul(vf));
  const mesh = new THREE.Mesh(geo, m);
  mesh.frustumCulled = false; mesh.renderOrder = 1;
  return mesh;
}

// ---------------------------------------------------------------- 2. cylindrical-billboard flame cards
/** `yaw` rotates the card about Y (second card: 35-55 deg); `opacity` scales it; `phase` offsets its time */
export function buildAuraCard(u: AuraUniforms, tex: AuraTextures, yaw: number, opacity: number, phase: number, order: number): THREE.Mesh {
  const m = mat();
  const { right, back } = cylFrame();
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const r2 = right.mul(c).add(back.mul(s)); // card plane direction, rotated about Y
  const p: U = positionLocal; // plane corners in [-0.5, 0.5]
  // table values x1.5 (was x1.15): at the gameplay camera (25 units out, 55°) the fire read as a small flame; the owner
  // wants an aura that surrounds him and grows each level (lead, 2026-11-02)
  const W = u.cardW.mul(M * 1.5), H = u.cardH.mul(M * 1.5);
  // pivot at 22 % of the height at the feet, pushed 0.12 m toward the camera; the up axis stays vertical (LEAN 0) or
  // leans toward the camera's screen-up for the steep gameplay camera (QA knob ?auralean=0..1)
  const upA: U = LEAN > 0 ? normalize(mix(vec3(0, 1, 0), camUpLocal(), LEAN)) : vec3(0, 1, 0);
  const P = FEET.add(back.mul(0.12 * M)).add(r2.mul(p.x.mul(W))).add(upA.mul(p.y.add(0.5 - 0.22).mul(H)));
  m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(P, 1)));
  // fire space: x across the card, y from the feet (22 % up the card) to the top
  const x = uv().x, v = uv().y.sub(0.22).div(0.78);
  const { heat, alpha, ember } = fire(u, tex, vec2(x, v), phase, yaw === 0 ? 1 : 1.3);
  m.colorNode = cel(u, heat, ember, alpha);
  // fire silhouette: full width at the base hugging the body, tapering above the head
  const taper = oneMinus(smoothstep(0.62, 1.0, abs(x.sub(0.5)).mul(2).div(oneMinus(clamp(v, 0, 1).mul(0.5)))));
  const edge = smoothstep(-0.02, 0.05, v).mul(smoothstep(0, 0.06, oneMinus(uv().y))); // nothing below the feet, soft top
  // the card passes in front of Aren: thinner over his body (centre, below the head) so his outline reads
  const body = oneMinus(oneMinus(smoothstep(0.1, 0.3, abs(x.sub(0.5)))).mul(oneMinus(smoothstep(0.35, 0.7, v))).mul(0.5));
  m.opacityNode = soft(celAlpha(alpha).mul(u.alpha).mul(1.25).mul(taper).mul(edge).mul(opacity).mul(body)); // cel fire needs more body than the strip did
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 1, 1), m);
  mesh.frustumCulled = false; mesh.renderOrder = order;
  return mesh;
}

// ---------------------------------------------------------------- 3. inner core glow shell
export function buildAuraCore(u: AuraUniforms): THREE.Mesh {
  const m = mat();
  const geo = new THREE.CylinderGeometry(0.75, 1, 1, 12, 1, true); geo.translate(0, 0.5, 0);
  const p: U = positionLocal;
  const P = FEET.add(V3(p.x.mul(0.4 * M), p.y.mul(2.1 * M), p.z.mul(0.4 * M)));
  const mv: U = modelViewMatrix;
  const posV = mv.mul(V4(P, 1)).xyz, nV = mv.mul(V4(normalize(V3(p.x, 0, p.z)), 0)).xyz;
  m.vertexNode = cameraProjectionMatrix.mul(V4(posV, 1));
  const facing = vary(dot(normalize(nV), normalize(posV.negate())), 'vCoreFace');
  m.colorNode = mix(u.mid, u.core, 0.7).mul(u.emission);
  const vf = smoothstep(0, 0.12, uv().y).mul(oneMinus(smoothstep(0.55, 1, uv().y)));
  const pulse = sin(time.mul(1.3)).mul(0.08).add(0.92); // slow pulse only
  m.opacityNode = u.glow.mul(vf).mul(pulse).mul(oneMinus(smoothstep(0, 0.6, facing).mul(0.5)));
  const mesh = new THREE.Mesh(geo, m);
  mesh.frustumCulled = false; mesh.renderOrder = 0;
  return mesh;
}

// ---------------------------------------------------------------- 4. rising streaks (pooled quads)
export function buildAuraStreaks(u: AuraUniforms, tex: AuraTextures): THREE.Mesh {
  const pos: number[] = [], uvs: number[] = [], info: number[] = [], idx: number[] = [];
  for (let i = 0; i < STREAK_POOL; i++) {
    const seed = (i * 0.618034 + 0.137) % 1, base = i * 4;
    for (const [cx, cy] of [[-1, 0], [1, 0], [1, 1], [-1, 1]]) { pos.push(cx, cy, 0); uvs.push(cx * 0.5 + 0.5, cy); info.push(seed, i / STREAK_POOL); }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setAttribute('streak', new THREE.Float32BufferAttribute(info, 2));
  geo.setIndex(idx);
  const m = mat();
  const { right } = cylFrame();
  const p: U = positionLocal, si: U = attribute('streak', 'vec2');
  const seed = si.x, active = step(si.y, u.streaks);
  // lifecycle: life 0.25-0.55 s, rise 2.2-4.5 m/s, width 0.02-0.06 m, height 0.3-0.9 m
  const L = seed.mul(0.3).add(0.25);
  const tt = time.div(L).add(seed.mul(9.1));
  const life = fract(tt), cyc = floor(tt);
  const th = hash(seed.mul(71.3).add(cyc.mul(0.73))).mul(TAU);
  const rad = hash(seed.mul(13.7).add(cyc)).mul(0.3).add(0.3).mul(u.shellR).mul(M).mul(0.9);
  const y0 = hash(seed.mul(3.3).add(cyc.mul(1.7))).mul(0.8).mul(M);
  const rise = hash(seed.mul(5.9).add(cyc)).mul(2.3).add(2.2).mul(M).mul(life).mul(L);
  const w = hash(seed.mul(8.1).add(cyc)).mul(0.04).add(0.02).mul(M).mul(active);
  const h = hash(seed.mul(2.7).add(cyc.mul(0.4))).mul(0.6).add(0.3).mul(M).mul(active);
  const base = FEET.add(V3(cos(th).mul(rad), y0.add(rise), sin(th).mul(rad)));
  const P = base.add(right.mul(p.x.mul(w))).add(V3(0, p.y.mul(h), 0));
  m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(P, 1)));
  const lifeV = vary(life, 'vStreakLife');
  const t: U = texture(tex.streak, uv());
  m.colorNode = mix(u.mid, u.core, uv().y.mul(0.6).add(0.2)).mul(u.emission);
  const fade = smoothstep(0, 0.15, lifeV).mul(oneMinus(smoothstep(0.55, 1, lifeV)));
  m.opacityNode = t.a.mul(fade).mul(0.7).mul(min(u.alpha.mul(2), 1));
  const mesh = new THREE.Mesh(geo, m);
  mesh.frustumCulled = false; mesh.renderOrder = 3;
  return mesh;
}

// ---------------------------------------------------------------- 5. ground rings (pooled emitter, no light)
export function buildAuraRings(u: AuraUniforms, tex: AuraTextures): THREE.Mesh {
  const pos: number[] = [], uvs: number[] = [], ring: number[] = [], idx: number[] = [];
  for (let i = 0; i < RING_POOL; i++) {
    const base = i * 4;
    for (const [cx, cy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) { pos.push(cx, 0, cy); uvs.push(cx * 0.5 + 0.5, cy * 0.5 + 0.5); ring.push(i); }
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setAttribute('ring', new THREE.Float32BufferAttribute(ring, 1));
  geo.setIndex(idx);
  const m = mat();
  const p: U = positionLocal, i: U = attribute('ring', 'float');
  // each pooled quad respawns every POOL/rate seconds, phase-shifted, so the pool emits `rate` rings per second
  const period = float(RING_POOL).div(max(u.ringRate, 0.1));
  const tt = time.div(period).add(i.div(RING_POOL));
  const life = fract(tt).mul(period), cyc = floor(tt);
  const k: U = clamp(life.div(RING_LIFE), 0, 1);
  const vis = step(life, RING_LIFE);
  const rot = hash(i.mul(7.1).add(cyc.mul(3.3))).mul(TAU);
  const c = cos(rot), s = sin(rot);
  // radius 0.55 m -> 1.15 m (scaled by the level's shell radius / Radiant's)
  const radius = k.mul(0.6).add(0.55).mul(M).mul(u.shellR.div(0.58)).mul(vis);
  const x = p.x.mul(c).sub(p.z.mul(s)), z = p.x.mul(s).add(p.z.mul(c));
  const P = FEET.add(V3(x.mul(radius), 0.05, z.mul(radius)));
  m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(P, 1)));
  const kV = vary(k, 'vRingK');
  const t: U = texture(tex.ring, uv());
  m.colorNode = mix(u.mid, u.core, 0.2).mul(u.emission);
  m.opacityNode = t.a.mul(oneMinus(kV)).mul(0.24).mul(clamp(u.rings, 0, 1)); // peak alpha < 0.25
  const mesh = new THREE.Mesh(geo, m);
  mesh.frustumCulled = false; mesh.renderOrder = 0;
  return mesh;
}
